/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/button-has-type */
/* eslint-disable react/prop-types */
/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import { Checkbox, IconButton, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { LoadingButton } from '@mui/lab';
import { SearchBox } from '../shared/SharedComponents';

const PAGE_SIZE = 10;

// ── useDebounce ───────────────────────────────────────────────────────────────
function useDebounce(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ── Mini pagination ───────────────────────────────────────────────────────────
const ModalPagination = ({ page, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-6 py-2 border-t border-gray-100 bg-gray-50">
      <span className="text-[12px] text-gray-400">Page {page} of {totalPages}</span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          className="px-3 py-1 rounded-[6px] text-[12px] font-medium border border-gray-200 bg-white text-gray-600 cursor-pointer hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          ‹ Prev
        </button>
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page === totalPages}
          className="px-3 py-1 rounded-[6px] text-[12px] font-medium border border-gray-200 bg-white text-gray-600 cursor-pointer hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Next ›
        </button>
      </div>
    </div>
  );
};

const AddAssessmentModal = ({
  open,
  onClose,
  onAdd,
  existingIds = [],
  fetchFn,
  tier,
  assessmentTypes = [],
  audienceIds = ['B2C', 'BOTH'],
  subscriptionTypeCode = '',
}) => {
  const [search, setSearch] = useState('');
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [adding, setAdding] = useState(false);

  const debouncedSearch = useDebounce(search);
  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Reset when modal opens
  useEffect(() => {
    if (open) {
      setSelected([]);
      setSearch('');
      setPage(1);
    }
  }, [open]);

  // Fetch on open, page, or debounced search change
  useEffect(() => {
    if (open) fetchList(page, debouncedSearch, tier);
  }, [open, page, debouncedSearch, tier]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const fetchList = async (pageNum = 1, searchVal = '', tier = '') => {
    setLoading(true);
    try {
      if (fetchFn) {
        const res = await fetchFn({ page: pageNum, pageSize: PAGE_SIZE, search: searchVal, audience: audienceIds, assessment_tier: tier });
        if (res?.ok) {
          const rows = res?.data?.rows ?? res?.data ?? [];
          const count = res?.data?.total ?? res?.total ?? rows.length;
          // setList(rows);
          const filtered = rows.filter((item) => item?.status_type === 1);
          setList(filtered);
          setTotal(count);
        }
      }
    } catch (e) {
      console.error('[AddAssessmentModal fetchList]', e);
    }
    setLoading(false);
  };

  // Client-side filter for already-added IDs (search handled server-side)
  const existingSet = new Set((existingIds || []).map((id) => String(id)));

  // Show ALL items from the server — existing ones are disabled in-place, not filtered out
  const toggle = (id) => {
    if (existingSet.has(String(id))) return;
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleAdd = async () => {
    setAdding(true);
    await onAdd(selected);
    setAdding(false);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/35">
      <div className="bg-white rounded-xl w-[520px] max-h-[90vh] flex flex-col border border-gray-200">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <p className="text-[15px] font-bold text-gray-900">Add Assessment</p>
          <IconButton size="small" onClick={onClose} sx={{ color: '#6b7280' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        {/* Search */}
        <div className="px-6 pt-4 pb-2">
          <SearchBox
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search assessments..."
          />
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-6 pb-2">
          {loading ? (
            <div className="flex justify-center py-10">
              <CircularProgress size={20} sx={{ color: '#2563eb' }} />
            </div>
          ) : list.length === 0 ? (
            <div className="flex flex-col items-center py-10 gap-2 text-gray-400">
              <InboxOutlinedIcon sx={{ fontSize: '28px', color: '#d1d5db' }} />
              <p className="text-[13px]">
                {search ? 'No assessments match your search' : 'No assessments available.'}
              </p>
            </div>
          ) : (
            list.map((a, idx) => {
              const isExisting = existingSet.has(String(a.id));
              const isChecked = isExisting || selected.includes(a.id);

              return (
                <div
                  key={a.id}
                  role="button"
                  tabIndex={isExisting ? -1 : 0}
                  onClick={() => !isExisting && toggle(a.id)}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && !isExisting && toggle(a.id)}
                  className={`flex items-center py-[10px] rounded px-1 ${isExisting
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:bg-gray-50 cursor-pointer'
                    }`}
                  style={{ borderBottom: idx < list.length - 1 ? '1px solid #f3f4f6' : 'none' }}
                >
                  <Checkbox
                    size="small"
                    checked={isChecked}
                    disabled={isExisting}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggle(a.id)}
                    sx={{
                      p: '4px', mr: '10px',
                      color: '#d1d5db',
                      '&.Mui-checked': { color: isExisting ? '#9ca3af' : '#2563eb' },
                      '&.Mui-disabled': { color: '#9ca3af' },
                    }}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-[13px] font-medium text-gray-900">
                        {a.title || a.chapter_description}
                          {a.internaltitle && (<span className="ml-1 text-gray-500">({a.internaltitle})</span>)}
                      </p>
                      {isExisting && (
                        <span className="text-[10px] font-medium text-gray-400 bg-gray-100 px-[6px] py-[2px] rounded-full">
                          Already added
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-400 uppercase tracking-[0.04em] mt-[2px]">
                      {a.assessment_type_code || a.assessmentType || ''}
                      {(a.assessment_sub_type_description || a.assessmentSubType) && (
                        <span className="ml-[6px] text-gray-300">
                          · {a.assessment_sub_type_description || a.assessmentSubType}
                        </span>
                      )}
                    </p>
                  </div>
                  {a.planCount > 1 && (
                    <span className="inline-flex items-center h-5 text-[10px] font-semibold rounded-full px-2 bg-amber-50 text-amber-800 border border-amber-300 gap-1 ml-2">
                      <span className="text-[12px]">⚠</span> In {a.planCount} plans
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        <ModalPagination page={page} totalPages={totalPages} onPageChange={setPage} />

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
          <p className="text-[13px] text-gray-500">
            {selected.length} selected
            {total > 0 && <span className="ml-2 text-gray-400">· {total} total</span>}
          </p>
          <div className="flex gap-[10px]">
            <button
              onClick={onClose}
              className="bg-transparent border border-gray-300 rounded-[7px] px-4 py-2 text-[13px] text-gray-700 cursor-pointer hover:bg-gray-50"
            >
              Cancel
            </button>
            <LoadingButton
              loading={adding}
              variant="contained"
              onClick={handleAdd}
              disabled={selected.length === 0}
              sx={{
                background: '#2563eb', borderRadius: '7px', textTransform: 'none',
                fontSize: '13px', fontWeight: 600, px: '20px',
                '&:hover': { background: '#1d4ed8' },
              }}
            >
              Add Selected
            </LoadingButton>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AddAssessmentModal;